import { Portal } from 'components/Portal/Portal';
import { QuerySwitch } from 'components/QuerySwitch';
import { IProps } from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups';
import {
  loadedAvatarCache,
  searchTeamMembersGroupsPopperModifiers,
} from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/SearchTeamMembersGroups.constants';
import {
  CloseIconWrapper,
  DropdownWrapper,
  SearchTeamMembersGroupsWrapper,
} from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/SearchTeamMembersGroups.styled';
import { TeamMembersList } from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/src/TeamMemberList';
import { useClickOutside } from 'hooks';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { SearchTeamMembersForGroupQuery } from 'types/graphql';
import { mergeRefs } from 'utils';
import { getJ360ElementNode } from 'utils/getJ360ElementNode';
import { useGroupsModalFormContext } from 'pages/Settings/modals/GroupsModal/GroupsModal.form.context';
import {
  BKJSSFBarSearchInputWrapper,
  StyledBKJSSFBarSearchInput,
} from 'components/BKJSSFBarSearchInput/BKJSSFBarSearchInput.styled';
import { css } from 'styled-components';
import { BKJIcon } from '@bookjane2/bookjane-design-library';
import { IconButton } from 'components/IconButton';
import { SearchTeamMembersGroupsContext } from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/SearchTeamMembersGroups.context';

export const SearchTeamMembersGroupsComponent: FC<IProps> = ({
  name,
  onChange,
  value,
  placeholder,
  ...rest
}) => {
  const {
    values: queryContextValues,
    onChange: queryContextOnChange,
    loadMore,
  } = useContext(SearchTeamMembersGroupsContext);

  const { formMembers } = useGroupsModalFormContext();

  const dropdownRef = useRef<HTMLElement | null>(null);

  const [referenceElement, setReferenceElement] = useState<Element | null>(null);
  const [popperElement, setPopperElement] = useState(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(
    getJ360ElementNode('dropdown-root'),
  );
  const [isOpen, setOpen] = useState(false);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: searchTeamMembersGroupsPopperModifiers,
  });

  const debounceTimerRef = useRef<number | null>(null);
  const [localSearchValue, setLocalSearchValue] = useState<string>('');

  useEffect(() => {
    setLocalSearchValue(queryContextValues['byMultipleFields'] || '');
  }, [queryContextValues]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!targetElement) {
      const element = document.createElement('div');
      element.setAttribute('id', 'dropdown-root');
      document.body.appendChild(element);
      setTargetElement(element);
    }

    // Clear avatar cache when dropdown component unmounts
    return () => {
      loadedAvatarCache.clear();
    };
  }, []); // eslint-disable-line

  const handleDropdownOpen = useCallback(() => {
    if (!isOpen) setOpen(true);
  }, [isOpen]);

  const handleDropdownClose = useCallback(() => {
    if (isOpen) setOpen(false);
  }, [isOpen]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      // Update display value
      setLocalSearchValue(newValue);

      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce context onChange
      debounceTimerRef.current = window.setTimeout(() => {
        queryContextOnChange({
          target: {
            name: 'byMultipleFields',
            value: newValue,
          },
        });
      }, 300);

      handleDropdownOpen();
    },
    [queryContextOnChange, handleDropdownOpen],
  );

  const handleSelect = useCallback(
    (event) => {
      const { value: nextValue } = event.target;
      const val = value === nextValue ? '' : event.target.value;
      onChange({ ...event, target: { ...event.target, name, value: val } });
    },
    [name, onChange, value],
  );

  useClickOutside(
    [dropdownRef && dropdownRef.current, targetElement && targetElement],
    handleDropdownClose,
  );

  const TeamMembersListWithProps = useCallback(
    ({ data }: { data: SearchTeamMembersForGroupQuery }) => (
      <TeamMembersList
        groupMemberIds={formMembers.map((member) => member.id)}
        data={data}
        onLoadMore={loadMore}
        name={name}
        onSelect={handleSelect}
      />
    ),
    [formMembers, handleSelect, loadMore, name],
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearchValue('');
    onChange({ target: { value: '', name: 'byMultipleFields' } });
    queryContextOnChange({ target: { value: '', name: 'byMultipleFields' } });

    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
  }, [queryContextOnChange, onChange]);

  const [isFocused, setFocus] = useState<boolean>(false);

  const onFocused = useCallback(() => {
    if (!isFocused) setFocus(true);
  }, [isFocused]);

  const onBlur = useCallback(() => {
    if (isFocused) setFocus(false);
  }, [isFocused]);

  return (
    <SearchTeamMembersGroupsWrapper
      ref={setReferenceElement}
      isOpen={!!isOpen && !!targetElement}
      onClick={handleDropdownOpen}
    >
      <BKJSSFBarSearchInputWrapper
        variant="modal"
        css={css`
          height: 36px;
          min-height: 36px;
          max-height: 36px;
        `}
      >
        <BKJIcon iconName="Search" />
        <StyledBKJSSFBarSearchInput
          {...rest}
          css={css`
            padding-left: 8px;
          `}
          name="byMultipleFields"
          onChange={handleChange}
          onFocus={onFocused}
          onBlur={onBlur}
          role="combobox"
          type="text"
          placeholder={placeholder}
          value={localSearchValue}
          autoComplete="off"
        />
        {!!localSearchValue && (
          <CloseIconWrapper>
            <IconButton iconName="CloseBold" onClick={handleClearSearch} />
          </CloseIconWrapper>
        )}
      </BKJSSFBarSearchInputWrapper>
      {!!isOpen && !!targetElement && (
        <Portal targetElement={targetElement}>
          <DropdownWrapper
            ref={mergeRefs([dropdownRef, setPopperElement])}
            style={{ ...styles.popper }}
            {...attributes.popper}
          >
            <QuerySwitch
              loadingIconSize="XSmall"
              context={SearchTeamMembersGroupsContext}
              component={TeamMembersListWithProps}
            />
          </DropdownWrapper>
        </Portal>
      )}
    </SearchTeamMembersGroupsWrapper>
  );
};
